/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2014, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'underscore',
	'chronology',
	'./util/EventConstants',
	'../lib/@waca/dashboard-common/dist/core/APIContext'
], (_, Chronology, EventConstants, APIContext) => {

	const STACK_SIZE_LIMIT = 150;

	class UndoRedoController {
		constructor(canvasModel, options = {}) {
			//Events to exclude from the undo/redo stack
			this.undoRedoExclusionList = { 'change:name': 1, 'change:id': 1, 'change:_meta': 1 };

			this.canvasModel = canvasModel;
			this.transactionApi = options.transaction;
			this.listeners = {};
			this._removeApiEvents = [];
			this.canvasModel.on('all', this.addToUndoRedoStack.bind(this), this.canvasModel);

			this.logger = options.logger;
			this.currentMode = options.mode || 'canvasMode';
			this.isStateDirty = options.isStateDirty || false;
			this.undoRedoStacks = {};
			this.changeModeName = EventConstants.changeModeAction;
			//Ensure the stack is created first
			this._getUndoRedoStack();
			return this.getPublicProperties();

		}

		destroy() {
			this._removeApiEvents.forEach(remove => remove && (typeof remove === 'function') && remove());
		}

		registerAPIEvents(api){
			if(api && api.on){
				const fRemove = api.on('all', (event) => {
					// For now, we only support the operations that explicitly indicate the undo/redo support
					if (event.info && event.info.supportsUndoRedo){
						this._addAPIEventToUndoRedo(api, event);
					}
				});
				this._removeApiEvents.push(fRemove);
			}
		}

		_addAPIEventToUndoRedo(api, event){
			// the event must have a tracking and a reversAction to support undo/redo
			if (event.tracking && event.tracking.undoActions ){
				this.addToUndoRedoStack(this._convertAPIEventToUndoAction(api, event));
			}
		}

		// Create an action object that is compatible with the existing undo/redo
		// This object will have a "applyFn" that will do the actual undo/redo based on the info in the event
		_convertAPIEventToUndoAction(api, event) {
			const undoRedoTransactionId = (event &&  event.transactionToken && event.transactionToken.transactionId);
			return  {
				value: 'redo', // dummy value to identify that we doing a redo
				prevValue: 'undo', // dummy value to identify that we doing an undo
				data: {
					undoRedoTransactionId: undoRedoTransactionId,
					transactionToken: event.transactionToken,
					transactionParamIndex: event.info.transactionParamIndex
				},
				sender: (event.context && event.context.undoRedo) ? 'UndoRedoController' : 'api',
				senderContext: {
					applyFn: this._apiEventApplyFn.bind(this, api, event)
				}
			};
		}

		_apiEventApplyFn(api, event, value, sender, name, data ) {
			let response;
			// if the operartion is part of a transaction,
			// the undoRedoController will create a transaction and pass it here
			const transactionToken = data && data.transactionToken;

			// Create an api context that is used to identify that the context of the operation
			// is undo/redo so that we don't add it again
			const apiContext = new APIContext({
				undoRedo: true,
				transactionToken
			});

			// Start with the canvas as a root API since we are listening on the canvas object
			let apiStack = this._executeTrace(event.tracking.callStack, [api]);

			if (value === 'undo'){
				//undo
				const undoActions = event.tracking.undoActions;
				let actionsToExecute = [];
				undoActions.forEach((undoAction) => {
					actionsToExecute.push({
						fn: this._executeAction.bind(this),
						args: [undoAction, apiStack, apiContext]
					});
				});
				response = this._executeActions(actionsToExecute);
			} else {
				// redo
				const action = event.tracking.action;
				response = this._executeAction(action, apiStack, apiContext);
			}

			if (response instanceof Promise){
				response = response.catch((err) => {
					this.logger.error('An error occured while executing an API undo trace', err);
					throw err;
				});
			}
			return response;
		}

		_executeTrace(callStack, apiStack = []){
			const localCopyApiStack = [...apiStack];
			if(callStack){
				callStack.forEach((step) => {
					const currentAPI = localCopyApiStack[localCopyApiStack.length -1];
					if(step && step.name === '..'){
						localCopyApiStack.pop();
					} else if (step && currentAPI && typeof(currentAPI[step.name]) === 'function'){
						// execute the step in the current context
						localCopyApiStack.push(currentAPI[step.name].apply(currentAPI, step.params ));
					}
				});
			}
			return localCopyApiStack;
		}

		_executeAction(action, apiStack, apiContext){
			let response;
			// If the action has a stack callStack, then execute to get the api context
			const newApiStack = this._executeTrace(action.callStack, apiStack);
			const currentAPI = newApiStack[newApiStack.length - 1];
			if (action && currentAPI && typeof(currentAPI[action.name]) === 'function') {
				// event objects are frozen.. so take a copy of the parameters bebore passing to the API.
				const params = JSON.parse(JSON.stringify(action.params || []));
				params.unshift(apiContext);
				response = currentAPI[action.name].apply(currentAPI, params);
				// if we have result actions, go over the response and execute the actions
				if (action.resultActions){
					const responseList = Array.isArray(response) ? response : [response];
					responseList.forEach((resultObject, index) => {
						const actions = action.resultActions[index];
						if (actions){
							actions.forEach(resultAction => {
								this._executeAction(resultAction, resultObject, apiContext);
							});
						}
					});
				}
			}else {
				this.logger.warn('undo/redo: missing action in event or action does not exist in API ', action, currentAPI);
			}
			return response;
		}

		getPublicProperties () {
			return {
				addToUndoRedoStack: this.addToUndoRedoStack.bind(this),
				clearStack: this.clearStack.bind(this),
				addListener: this.addListener.bind(this),
				removeListener: this.removeListener.bind(this),
				canUndo: this.canUndo.bind(this),
				canRedo: this.canRedo.bind(this),
				undo: this.undo.bind(this),
				redo: this.redo.bind(this),
				setMode: this.setMode.bind(this),
				setDirty: this.setDirty.bind(this),
				registerSaveAction: this.registerSaveAction.bind(this),
				registerAPIEvents: this.registerAPIEvents.bind(this),
				isDirty: this.isDirty.bind(this),
				destroy: this.destroy.bind(this)
			};
		}

		_validateUndoRedoAction(action) {
			if (!action || !action.senderContext || !action.senderContext.applyFn) {
				return false;
			}

			if (action.sender === 'UndoRedoController') {
				return false;
			}

			if (!action.data || !action.data.transactionToken) {
				// TODO: no data means it's a valid transaction? confusing!!! re-evaluate this condition
				return true;
			}

			const { eventGenerated } = action.data.transactionToken || {};

			if (!eventGenerated) {
				return true;
			}

			return false;
		}

		/**
		 * Add the action to the undo/redo stack.
		 *
		 * If the action has a transaction id 'action.data.undoRedoTransactionId', then it will be combined with other actions that have the same id.
		 * Transactions retriggered by a secondary event will not be kept since the initial transaction is already part of the undo stack.
		 *
		 * Note that the undo/redo assumes that all actions within a transaction happen in the same thread (no setTimeout).
		 * If one action is using setTimeout and takes longer to execute and the user selects undo before it finishes,
		 * then the undo for the transaction will not include this action. The action will be added separately when it is completed.
		 *
		 * @param action
		 */
		addToUndoRedoStack (action) {
			if (this._validateUndoRedoAction(action)) {
				const upDownActions = this._checkTransaction(action);
				if (upDownActions){
					const undoRedoObj = {
						up: function() {
							if(!this.call){
								upDownActions.up(action.upValue ? action.upValue : action.value, 'UndoRedoController', action.name);
							}
						},
						down: function() {
							upDownActions.down(action.prevValue, 'UndoRedoController', action.name);
						},
						call: false,
						name: action.name,
						prevDirtyState: this.isStateDirty
					};

					this.logger.debug('UndoRedoController:addToUndoRedoStack', action.data ? action.data.undoRedoTransactionId : action.data, action.name, action.value, action.prevValue);
					const stack = this._getUndoRedoStack();
					stack.add(undoRedoObj);
				}
			}
		}

		setDirty(bool) {
			this.isStateDirty = bool;
		}

		clearStack () {
			const stack = this._getUndoRedoStack();
			if(stack){
				stack.clear();
			}
			_.each(this.listeners, (listener) => {
				listener.onStateChange(this);
			});
		}

		_checkTransaction (action) {
			if (action.data && (action.data.saveOnly || action.data.skipUndoRedo || action.data.runtimeOnly)) {
				return null;
			}

			const upDownAction = {
				up: action.senderContext.applyFn,
				down: action.senderContext.applyFn

			};
			const transactionId = action.data ? action.data.undoRedoTransactionId : null;
			if (transactionId){
				const transactionsMap = this._getTransactionsMap();
				let actions = transactionsMap[transactionId];
				if(actions){
					actions.push(action);
					return null;
				}

				actions = [action];
				transactionsMap[transactionId] = actions;

				upDownAction.up = function(){
					this._redoTransaction(actions);
				}.bind(this);
				upDownAction.down = function(){
					// cleanup the transactions list
					delete transactionsMap[transactionId];
					this._undoTransaction(actions);
				}.bind(this);
			}

			return upDownAction;
		}

		_redoTransaction (actions) {
			const transactionToken = this.transactionApi.startTransaction();
			let action, applyAction;
			let actionsToExecute = [];
			for(let i = 0 ; i < actions.length; i++){
				action = actions[i];
				applyAction = action.senderContext.applyFn;
				if (applyAction){
					action.data.transactionToken = transactionToken;
					action.data.undoRedoTransactionId = transactionToken.transactionId;
					action.data.undoRedoTransactionType = 'redo';
					actionsToExecute.push({
						fn: applyAction,
						args:[action.upValue ? action.upValue : action.value, 'UndoRedoController', action.name, action.data]
					});
				}
			}
			const response =  this._executeActions(actionsToExecute);
			if (response instanceof Promise){
				this.lastUndoRedoOpPromise = response.catch(err => {
					this.logger.error('An error occured while executing the redo stack', err);
					throw err;
				}).finally(() => { this.transactionApi.endTransaction(transactionToken); });
			} else {
				this.transactionApi.endTransaction(transactionToken);
			}
		}

		_undoTransaction (actions) {
			const transactionToken = this.transactionApi.startTransaction();
			let action, applyAction;
			let actionsToExecute = [];
			for(let i = actions.length - 1; i >= 0; i--){
				action = actions[i];
				applyAction = action.senderContext.applyFn;
				if (applyAction){
					action.data.transactionToken = transactionToken;
					action.data.undoRedoTransactionId = transactionToken.transactionId;
					action.data.undoRedoTransactionType = 'undo';
					actionsToExecute.push({
						fn: applyAction,
						args:[action.prevValue, 'UndoRedoController', action.name, action.data]
					});
				}
			}
			const response = this._executeActions(actionsToExecute);
			if (response instanceof Promise){
				this.lastUndoRedoOpPromise = response.catch(err => {
					this.logger.error('An error occured while executing the undo stack', err);
					throw err;
				}).finally(() => { this.transactionApi.endTransaction(transactionToken); });
			} else {
				this.transactionApi.endTransaction(transactionToken);
			}
		}


		// Execute a series of async actions sequentially .. 
		_executeActions(actions, startIndex = 0) {
			let response;
			while (startIndex < actions.length ){
				response = actions[startIndex].fn(...actions[startIndex].args);
				startIndex++;
				if (response instanceof Promise){
					// one of the action returned a promise.
					// break the loop and continue it after the promise is resolved
					response = response.then(() => {
						return this._executeActions(actions, startIndex);
					});
					break;
				}
			}
			return response;
		}

		addListener (id, listener) {
			if (typeof listener.onStateChange === 'function') {
				this.listeners[id] = listener;
			}
		}

		removeListener (id) {
			delete this.listeners[id];
		}

		canUndo () {
			const stack = this._getUndoRedoStack();
			return stack.undos.length > 0;
		}

		canRedo () {
			const stack = this._getUndoRedoStack();
			return stack.redos.length > 0;
		}

		_trackDirtyState (stack) {
			if (!stack || !stack[0]) {
				return;
			}
			const peek = stack[0].prevDirtyState;
			// We flip the state for moving undo items to the redo stack and vice versa
			stack[0].prevDirtyState = this.isStateDirty;
			if (stack[0].name !== this.changeModeName) {
				this.setDirty(peek);
			}
		}

		undo () {
			const stack = this._getUndoRedoStack();
			this._trackDirtyState(stack.undos);
			stack.undo();

			const promise = this.lastUndoRedoOpPromise  || Promise.resolve();
			if (this.lastUndoRedoOpPromise){
				this.lastUndoRedoOpPromise = null;
			}
			return promise;
		}

		redo () {
			const stack = this._getUndoRedoStack();
			this._trackDirtyState(stack.redos);
			stack.redo();
			const promise = this.lastUndoRedoOpPromise  || Promise.resolve();
			if (this.lastUndoRedoOpPromise){
				this.lastUndoRedoOpPromise = null;
			}
			return promise;
		}

		onAdd () {
			this.enableState('undo', true);
			this.enableState('redo', false);
		}

		enableState (state, enabled) {
			this.canvasModel.trigger('undo:state', { senderContext: this, 'stack': state, 'enabled': enabled });
			_.each(this.listeners, function(listener) {
				listener.onStateChange(this);
			}.bind(this));
		}

		setMode (mode) {
			let transactionsToMergeFrom;
			if (this.currentMode !== 'canvasMode') {
				transactionsToMergeFrom = this.undoRedoStacks[this.currentMode].transactions;
				delete this.undoRedoStacks[this.currentMode];
			}

			this.currentMode = mode || 'canvasMode';
			this._mergeUndoStacks(transactionsToMergeFrom);

			const stack = this._getUndoRedoStack();
			this.enableState('undo', stack.undos.length > 0);
			this.enableState('redo', stack.redos.length > 0);
		}

		registerSaveAction() {
			// When we save we need to cleanup the items in the undo stack and mark them all as dirty
			let stack = this._getUndoRedoStack();
			_.forEach(stack.undos, ele => {
				ele.prevDirtyState = true;
			});
		}

		isDirty() {
			/**
			 * Board Logic
			 * - We want to track the dirty/clean state of the DB from here
			 * - changeMode actions are added to the stack but dont count as 'dirty'
			 * all other actions count as dirty
			 * - If we save, the save action will clear the dirty flag and we need to track that boundary
			 * - We can have an initially dirty db if created from reloadFromJSONSpec
			 */
			return this.isStateDirty;
		}

		_getUndoRedoStack () {
			if (!this.undoRedoStacks[this.currentMode]) {
				this.undoRedoStacks[this.currentMode] = new Chronology({
					limit: STACK_SIZE_LIMIT,
					onAdd: this.onAdd.bind(this),
					onRedo: this.enableState.bind(this, 'undo', true),
					onUndo: this.enableState.bind(this, 'redo', true),
					onBegin: this.enableState.bind(this, 'undo', false),
					onEnd: this.enableState.bind(this, 'redo', false)
				});
				this.undoRedoStacks[this.currentMode].transactions = {};
			}
			return this.undoRedoStacks[this.currentMode];
		}

		_getTransactionsMap () {
			return this.undoRedoStacks[this.currentMode].transactions;
		}

		_mergeUndoStacks (transactionsToMergeFrom = {}) {
			_.each(transactionsToMergeFrom, actions => {
				actions.forEach(action => {
					this.addToUndoRedoStack(action);
				});
			});
		}

	}

	return UndoRedoController;
});
